import { errorBox, successBox } from '@core/components/AlertBox';
import { toHumanReadable } from '@core/logic';
import { CheckmarkCircleFilled } from '@deepup/icons';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { photosState } from '@photos/components/PhotosInViewport/state';
import { QAState, qaStates } from '@scans/types';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { useScanFilters } from '@projects/pages/detail/hooks';
import SMABulkDialog from '../../SmaNumbers/SMABulkDialog';
import { checkboxSelectionState, uncheckScans } from './ScansInScopeList/checkboxSelectionState';
import { useConnectScans } from './ScansInScopeList/connect';
import { ReworkStatus } from './ScansInScopeList/ReworkStatus';
import { Remarks } from './ScansInScopeList/ReworkStatus/Remarks';
import { fetchComment } from './ScansInScopeList/ScanDateGroupItem/ScanListItem/ScanListItem';

export const QAStateSelectorBar = () => {
  const { checkedScanIds, scans, updateScansQaState } = useConnectScans();
  const { qaState } = useScanFilters();
  const [nextScanQaStatus, setNextScanQaStatus] = useState<QAState>(
    qaState && qaState !== 'AUTO_DONE' && qaState !== 'SCAN_ONLY_DONE' ? qaState : 'DONE',
  );

  useEffect(() => {
    if (qaState && qaState !== 'AUTO_DONE' && qaState !== 'SCAN_ONLY_DONE') {
      setNextScanQaStatus(qaState);
    }
  }, [qaState]);
  const [remarks, setRemarks] = useState<string | null>(null);
  const [remarksEdited, setRemarksEdited] = useState(false);
  const [shouldDeleteAllRemarks, setShouldDeleteAllRemarks] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { selectedPhotos } = useSnapshot(photosState);

  const selectedScans = scans.filter((scan) => checkedScanIds.includes(scan.id));
  const selectedPhotosIds = selectedPhotos.map((photo) => photo.id);

  const dialogTitle = `Set SMAs for ${selectedPhotos.length} photo${
    selectedPhotos.length === 1 ? '' : 's'
  } and ${selectedScans.length} scan${selectedScans.length === 1 ? '' : 's'}`;

  const fetchAllRemarks = async (scanIds: string[]) => {
    const remarksMap: Record<string, string | null> = {};
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Timed out loading scan remarks')), 30_000);
    });

    try {
      await Promise.race([
        Promise.all(
          scanIds.map(async (scanId) => {
            const remark = await fetchComment(scanId);
            remarksMap[scanId] = remark;
          }),
        ),
        timeout,
      ]);
      return remarksMap;
    } finally {
      clearTimeout(timeoutId!);
    }
  };

  const getRemarksMap = async (
    scanIds: string[],
    currentRemarks: string | null,
    remarksWereEdited: boolean,
    deleteAll: boolean,
  ) => {
    const batchRemarks: Record<string, string | null> = {};
    if (deleteAll) {
      scanIds.forEach((scanId) => {
        batchRemarks[scanId] = null;
      });
      return batchRemarks;
    }
    if (remarksWereEdited) {
      scanIds.forEach((scanId) => {
        batchRemarks[scanId] = currentRemarks;
      });
      return batchRemarks;
    }
    return fetchAllRemarks(scanIds);
  };

  const selectedScanIdsKey = checkedScanIds.join(',');

  useEffect(() => {
    let cancelled = false;
    const selectedIds = selectedScanIdsKey ? selectedScanIdsKey.split(',') : [];

    setRemarks(null);
    setRemarksEdited(false);
    if (selectedIds.length !== 1) {
      setShouldDeleteAllRemarks(false);
      return () => {
        cancelled = true;
      };
    }

    setShouldDeleteAllRemarks(false);
    const fetchRemarks = async () => {
      try {
        const scanRemarks = await fetchComment(selectedIds[0]);
        if (!cancelled) setRemarks(scanRemarks ?? '');
      } catch (error) {
        console.error('Error fetching remarks', error);
        if (!cancelled) setRemarks(null);
      }
    };
    fetchRemarks();
    return () => {
      cancelled = true;
    };
  }, [selectedScanIdsKey]);

  if (!checkedScanIds.length && !selectedPhotosIds.length) return null;

  if (selectedPhotosIds.length && !checkedScanIds.length) {
    return (
      <SMABulkDialog scansIds={checkedScanIds} photosIds={selectedPhotosIds} title={dialogTitle} />
    );
  }

  return (
    <>
      <Paper
        elevation={12}
        square
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1, // z-index is needed in order to prevent overlap from ScanListItems behind the QAStateSelectorBar!
          mx: -2,
          mb: -2,
        }}
      >
        <Stack mx={2} my={1} gap={1} direction="row" alignItems="center">
          <Typography flexGrow={1}>
            {isSubmitting
              ? 'Updating scan QA state…'
              : `${checkedScanIds.length} of ${scans.length} selected`}
          </Typography>
          <TextField
            select
            value={nextScanQaStatus}
            label="Set status"
            disabled={isSubmitting}
            onChange={(e) => setNextScanQaStatus(e.target.value as QAState)}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
              },
            }}
          >
            {qaStates
              .filter((state) => state !== 'AUTO_DONE' && state !== 'SCAN_ONLY_DONE')
              .map((status: QAState) => (
                <MenuItem key={status} value={status}>
                  {toHumanReadable(status)}
                </MenuItem>
              ))}
          </TextField>
          <IconButton
            data-testid="apply-scans-qa-state"
            disabled={isSubmitting}
            onClick={async () => {
              const submittedScans = checkboxSelectionState.checkedScans.map((scan) => ({
                ...scan,
                reworkCategories: [...scan.reworkCategories],
              }));
              if (!submittedScans.length) return;

              const submittedScanIds = submittedScans.map((scan) => scan.id);
              const submittedStatus = nextScanQaStatus;
              const submittedRemarks = remarks;
              const submittedRemarksEdited = remarksEdited;
              const deleteRemarks = shouldDeleteAllRemarks;

              setIsSubmitting(true);
              checkboxSelectionState.isSubmittingQaState = true;
              try {
                let remarksMap: Record<string, string | null>;
                try {
                  remarksMap = await getRemarksMap(
                    submittedScanIds,
                    submittedRemarks,
                    submittedRemarksEdited,
                    deleteRemarks,
                  );
                } catch (_error) {
                  errorBox(
                    'Could not load existing scan remarks. QA state was not changed. Please try again.',
                  );
                  return;
                }
                const success = await updateScansQaState(
                  submittedScans,
                  submittedStatus,
                  remarksMap,
                );
                if (success) {
                  uncheckScans(submittedScanIds);
                  setNextScanQaStatus('DONE');
                  setRemarks('');
                  setRemarksEdited(false);
                  setShouldDeleteAllRemarks(false);
                  successBox('Scans QA state updated successfully');
                }
              } catch (_error) {
                errorBox(
                  'Could not confirm the QA state update. Check scan statuses before retrying.',
                );
              } finally {
                checkboxSelectionState.isSubmittingQaState = false;
                setIsSubmitting(false);
              }
            }}
            color="primary"
            edge="end"
          >
            {isSubmitting ? (
              <CircularProgress size={22} aria-label="Updating scan QA state" />
            ) : (
              <CheckmarkCircleFilled />
            )}
          </IconButton>
        </Stack>

        {nextScanQaStatus === 'TO_REWORK' && <ReworkStatus />}
        <Remarks
          remarks={shouldDeleteAllRemarks ? '' : remarks || ''}
          setRemarks={(value) => {
            setRemarks(value);
            setRemarksEdited(true);
          }}
          shouldShowWarning={checkedScanIds.length > 1}
          shouldDeleteAllRemarks={shouldDeleteAllRemarks}
          setShouldDeleteAllRemarks={setShouldDeleteAllRemarks}
          disabled={isSubmitting}
        />
        <SMABulkDialog
          scansIds={checkedScanIds}
          photosIds={selectedPhotosIds}
          title={dialogTitle}
        />
      </Paper>
    </>
  );
};
